import type { CounterFieldConfig } from '@/features/equipment-types/types';
import type { CounterReading, DeltaState } from './types';

const NEAR_LIMIT_RATIO = 0.8;

/**
 * Live preview shown in the Record modal. Mirrors the backend
 * validation pipeline so the UI surfaces the same outcome before
 * the user clicks Submit. The backend remains authoritative — this
 * is purely UX.
 */
export function computeDeltaState(
  candidate: { value: number; readAt: string },
  last: CounterReading | null,
  cfg: CounterFieldConfig,
): DeltaState {
  if (!last) return { kind: 'first' };

  const lastValue = Number(last.value);
  const lastAt = new Date(last.readAt).getTime();
  const nextAt = new Date(candidate.readAt).getTime();
  const ms = nextAt - lastAt;
  // Same-day reading: clamp to a 1-hour minimum so the rate
  // ceiling stays meaningful and we don't divide by zero.
  const days = Math.max(ms / 86_400_000, 1 / 24);
  const delta = candidate.value - lastValue;
  const maxDelta = days * cfg.maxRatePerDay;

  if (cfg.monotonic && candidate.value < lastValue) {
    return {
      kind: 'error',
      delta,
      maxDelta,
      daysSinceLast: days,
      reason: 'decreasing',
      message: '',
    };
  }
  if (delta > maxDelta) {
    return {
      kind: 'error',
      delta,
      maxDelta,
      daysSinceLast: days,
      reason: 'rateExceeded',
      message: '',
    };
  }
  if (delta > NEAR_LIMIT_RATIO * maxDelta) {
    return {
      kind: 'warning',
      delta,
      maxDelta,
      daysSinceLast: days,
      reason: 'rateNearLimit',
      message: '',
    };
  }
  return {
    kind: 'info',
    delta,
    maxDelta,
    daysSinceLast: days,
    message: '',
  };
}

/** Days since a reading. Returns null when the input is missing/invalid. */
export function daysSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return null;
  return Math.floor((Date.now() - d) / 86_400_000);
}

/** Decimal string → number safely, with a fallback. */
export function readingValueAsNumber(reading: CounterReading): number {
  const n = Number(reading.value);
  return Number.isNaN(n) ? 0 : n;
}
